// Registration.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Flex,
  VStack,
  Text,
  Button,
  Image,
} from "@chakra-ui/react";
import { useInView } from "react-intersection-observer";
import arrow from "./Images/Button-Arrow.svg";

export const Registration = () => {
  const steps = [
    {
      title: "Choose a Course",
      description: "Select the course you want to join and submit your request.",
    },
    {
      title: "Receive Course Details",
      description:
        "We’ll send you an email with all the important information, including fees, course details, and next steps.",
    },
    {
      title: "Complete Registration & Enjoy Benefits",
      description:
        "Once registered, you can add or drop courses as needed and take advantage of our exclusive discounts.",
    },
  ];

  const [ref1, inView1] = useInView({ threshold: 0, rootMargin: "-50% 0px -50% 0px" });
  const [ref2, inView2] = useInView({ threshold: 0, rootMargin: "-50% 0px -50% 0px" });
  const [ref3, inView3] = useInView({ threshold: 0, rootMargin: "-50% 0px -50% 0px" });
  const refs = [ref1, ref2, ref3];
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
  }, []);

  useEffect(() => {
    if (inView1) setActiveStep(0);
    else if (inView2) setActiveStep(1);
    else if (inView3) setActiveStep(2);
  }, [inView1, inView2, inView3]);

  return (
    <Box
      id="registration"
      w="100%"
      px={{ base: "16px", md: "45px" }}
      mt="60px"
      py={{ base: "40px", md: "0" }}
    >
      <Flex
        direction={{ base: "column", lg: "row" }}
        gap={{ base: "24px", lg: "60px" }}
        align="flex-start"
        justify="space-between"
      >
        {/* Left column */}
        <VStack align="flex-start" w={{ base: "100%", lg: "50%" }} gap="14px">
          <Heading
            color="#161616"
            fontSize={{ base: "20px", md: "32px" }}
            fontWeight="550"
          >
            Registration Process
          </Heading>
          <Text
            w={{ base: "100%", md: "578px" }}
            color="#343634"
            fontSize={{ base: "12px", md: "18px" }}
            fontWeight="400"
            lineHeight="1.4"
          >
            Our registration process is simple. With a click of a button, you’re in. It all starts with the course you would like to join. We reach out to you with more information and get you all set up.
          </Text>
          <Button
            as="a"
            href="/courses"
            px={{ base: "16px", md: "24px" }}
            py={{ base: "8px", md: "10px" }}
            borderRadius="24px"
            border="2.5px solid #E8E8E8"
            variant="outline"
          >
            <Flex align="center" gap="8px">
              <Text fontSize={{ base: "12px", md: "14px" }} fontWeight="400">
                Access our Courses
              </Text>
              <Image src={arrow} alt="button-arrow-icon" boxSize={{ base: "16px", md: "20px" }} />
            </Flex>
          </Button>
        </VStack>

        <Box w={{ base: "100%", lg: "50%" }}>
          <VStack
            spacing={{ base: "150px", md: "350px" }}
            align="flex-start"
          >
            {steps.map((step, i) => (
              <Flex key={i} ref={refs[i]} align="flex-start" w="100%">
                <Box
                  pos="relative"
                  display="flex"
                  w={{ base: "95px", md: "78px" }}
                  h={{ base: "78px", md: "78px" }}
                  p="34px"
                  flexDir="column"
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="full"
                  bg={i <= activeStep ? "#0069CA" : "#EBF2F7"}
                  transition="background-color 0.3s ease"
                >
                  <Text
                    fontSize={{ base: "18px", md: "24px" }}
                    fontWeight="400"
                    textAlign="center"
                    color={i <= activeStep ? "#FFFFFF" : "#0069CA"}
                    transition="color 0.3s ease"
                  >
                    {i + 1}
                  </Text>
                  {i < steps.length - 1 && (
                    <Box
                      pos="absolute"
                      top="calc(100% + 12px)"
                      left="50%"
                      transform="translateX(-50%)"
                      w="6px"
                      h={{ base: "180px", md: "308px" }}
                      bg={i < activeStep ? "#0069CA" : "#D9D9D9"}
                      borderRadius="8px"
                      transition="background-color 0.3s ease"
                    />
                  )}
                </Box>

                <VStack align="flex-start" spacing="8px" ml="16px">
                  <Heading
                    as="h3"
                    fontSize={{ base: "16px", md: "24px" }}
                    fontWeight="550"
                    color="#161616"
                  >
                    {step.title}
                  </Heading>
                  <Text
                    fontSize={{ base: "12px", md: "14px" }}
                    fontWeight="400"
                    color="#343634"
                  >
                    {step.description}
                  </Text>
                </VStack>
              </Flex>
            ))}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

